import { useState } from 'react'
import {
  BarChart3, Coins, CreditCard, Landmark, Package, Percent, Receipt, RefreshCw,
  Wallet,
} from 'lucide-react'
import PlansPanel from './PlansPanel'
import CreditPacksPanel from './CreditPacksPanel'
import PricingCurrencyPanel from './PricingCurrencyPanel'
import CouponsDiscountsPanel from './CouponsDiscountsPanel'
import SubscriptionsPanel from './SubscriptionsPanel'
import PaymentProviderPanel from './PaymentProviderPanel'
import TaxPanel from './TaxPanel'
import WalletsPanel from './WalletsPanel'
import JobsPanel from './JobsPanel'
import UserUsagePanel from './UserUsagePanel'
import CloudCostPanel from './CloudCostPanel'
import ReadinessPanel from './ReadinessPanel'

type SubTab =
  | 'plans' | 'cloudcost' | 'packs' | 'pricing' | 'coupons' | 'subscriptions'
  | 'wallets' | 'usage' | 'provider' | 'tax' | 'jobs'

const SUB_TABS: Array<{ key: SubTab; label: string; icon: React.ReactNode }> = [
  { key: 'plans', label: 'Plans', icon: <Package size={14} /> },
  { key: 'cloudcost', label: 'Cloud Cost', icon: <Landmark size={14} /> },
  { key: 'packs', label: 'Credit packs', icon: <Coins size={14} /> },
  { key: 'pricing', label: 'Credit conversion', icon: <Landmark size={14} /> },
  { key: 'coupons', label: 'Coupons & discounts', icon: <Percent size={14} /> },
  { key: 'subscriptions', label: 'Subscriptions', icon: <RefreshCw size={14} /> },
  { key: 'wallets', label: 'Wallets', icon: <Wallet size={14} /> },
  { key: 'usage', label: 'User usage', icon: <BarChart3 size={14} /> },
  { key: 'provider', label: 'Provider', icon: <CreditCard size={14} /> },
  { key: 'tax', label: 'Tax', icon: <Receipt size={14} /> },
  { key: 'jobs', label: 'Jobs', icon: <RefreshCw size={14} /> },
]

/**
 * Everything about money, in one tab.
 *
 * The instance admin is the ONLY person who configures pricing (decision D1):
 * organization admins assign users and see balances, they never set a price.
 */
export default function BillingAdminTab() {
  const [tab, setTab] = useState<SubTab>('plans')

  return (
    <div>
      <ReadinessPanel onOpenSub={(sub) => setTab(sub as SubTab)} />
      <div style={{
        display: 'flex', gap: '0.25rem', flexWrap: 'wrap',
        borderBottom: '1px solid var(--color-border)', marginBottom: '1.25rem',
      }}>
        {SUB_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.4rem 0.75rem', background: 'none', border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--color-accent)' : '2px solid transparent',
              color: tab === t.key ? 'var(--color-text)' : 'var(--color-text-secondary)',
              fontWeight: tab === t.key ? 600 : 400,
              fontSize: '0.875rem', cursor: 'pointer', marginBottom: -1,
            }}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {tab === 'plans' && <PlansPanel />}
      {tab === 'cloudcost' && <CloudCostPanel />}
      {tab === 'packs' && <CreditPacksPanel />}
      {tab === 'pricing' && <PricingCurrencyPanel />}
      {tab === 'coupons' && <CouponsDiscountsPanel />}
      {tab === 'subscriptions' && <SubscriptionsPanel />}
      {tab === 'wallets' && <WalletsPanel />}
      {tab === 'usage' && <UserUsagePanel />}
      {tab === 'provider' && <PaymentProviderPanel />}
      {tab === 'tax' && <TaxPanel />}
      {tab === 'jobs' && <JobsPanel />}
    </div>
  )
}
